import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Send, ArrowLeft } from "lucide-react"
import { MessageBubble } from "./MessageBubble"
import { useMessages, useSendMessage } from "@/hooks/useMessages"

import { useAuth } from "@/hooks/useAuth"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"

interface ChatInterfaceProps {
  conversationId: string
  recipientName: string
  onBack?: () => void
}

export const ChatInterface = ({ 
  conversationId, 
  recipientName, 
  onBack 
}: ChatInterfaceProps) => {
  const [message, setMessage] = useState("")
  const scrollRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { profile } = useAuth()
  
  const { data: messages = [], isLoading } = useMessages(conversationId)
  const sendMessageMutation = useSendMessage()
  
  // O Hook de tempo real já está sendo chamado no componente pai
  // Não chamar aqui para evitar subscrições duplicadas

  // Auto-scroll para última mensagem
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSendMessage = async () => {
    if (!message.trim()) return

    try {
      await sendMessageMutation.mutateAsync({
        conversation_id: conversationId,
        content: message.trim(),
        message_type: "text"
      })
      setMessage("")
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error)
      toast.error("Erro ao enviar mensagem")
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigate("/mensagens")
    }
  }

  return (
    <Card className="h-[calc(100vh-120px)] flex flex-col">
      <CardHeader className="flex-row items-center gap-2 pb-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleBack}
          className="p-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <CardTitle className="text-lg">{recipientName}</CardTitle>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0">
        <ScrollArea 
          ref={scrollRef}
          className="flex-1 px-4"
        >
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <p className="text-muted-foreground">Carregando mensagens...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex justify-center items-center h-32">
              <p className="text-muted-foreground">
                Nenhuma mensagem ainda. Inicie a conversa!
              </p>
            </div>
          ) : (
            <div className="py-4">
              {messages.map((msg: any) => (
                <MessageBubble
                  key={msg.id}
                  content={msg.content}
                  senderName={msg.sender?.full_name || "Usuário"}
                  createdAt={msg.created_at}
                  isOwnMessage={msg.sender_id === profile?.id}
                  messageType={msg.message_type}
                />
              ))}
            </div>
          )}
        </ScrollArea>
        
        <div className="border-t p-4">
          <div className="flex gap-2">
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua mensagem..."
              disabled={sendMessageMutation.isPending}
              className="flex-1"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!message.trim() || sendMessageMutation.isPending}
              size="sm"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}